/**
 * Case-, diacritics- and whitespace-insensitive key used to compare titles and
 * author names (likely-duplicate detection, find-or-create by name).
 */
export function normalizeKey(value: string): string {
  return value.normalize("NFKD").replace(/\p{M}/gu, "").toLowerCase().replace(/\s+/g, " ").trim();
}

const ARTICLES = ["the", "an", "a", "der", "die", "das", "eine", "ein"];

/** Moves a leading English/German article to the end: "The Colour of Magic" -> "Colour of Magic, The". */
export function sortTitle(title: string): string {
  const trimmed = title.trim();
  const match = /^(\S+)\s+(.+)$/su.exec(trimmed);
  if (!match) return trimmed;
  const [, first, rest] = match as unknown as [string, string, string];
  if (!ARTICLES.includes(first.toLowerCase())) return trimmed;
  return `${rest}, ${first}`;
}

export interface AuthorName {
  /** Display name, e.g. `Terry Pratchett`. */
  name: string;
  /** Sort name, e.g. `Pratchett, Terry`. */
  sortName: string;
}

/**
 * Derives the display and sort name of an author. A name with exactly one comma
 * is taken as `Last, First` and inverted; otherwise the last word is the family name.
 * An explicit `fileAs` value (EPUB `file-as`) wins as sort name.
 */
export function parseAuthorName(raw: string, fileAs?: string): AuthorName {
  const clean = raw.replace(/\s+/g, " ").trim();
  const sortOverride = fileAs?.replace(/\s+/g, " ").trim() || undefined;
  const parts = clean.split(",");
  if (parts.length === 2) {
    const last = (parts[0] ?? "").trim();
    const first = (parts[1] ?? "").trim();
    if (last && first) {
      return { name: `${first} ${last}`, sortName: sortOverride ?? `${last}, ${first}` };
    }
  }
  if (sortOverride) return { name: clean, sortName: sortOverride };
  const words = clean.split(" ");
  if (words.length < 2) return { name: clean, sortName: clean };
  const family = words[words.length - 1] as string;
  return { name: clean, sortName: `${family}, ${words.slice(0, -1).join(" ")}` };
}

// --- identifier and date validation (shared by ingest and metadata editing) ---

export function isIsbn10Valid(digits: string): boolean {
  if (!/^\d{9}[\dX]$/.test(digits)) return false;
  let sum = 0;
  for (let i = 0; i < 10; i++) {
    const c = digits[i] as string;
    sum += (10 - i) * (c === "X" ? 10 : Number(c));
  }
  return sum % 11 === 0;
}

export function isIsbn13Valid(digits: string): boolean {
  if (!/^\d{13}$/.test(digits)) return false;
  let sum = 0;
  for (let i = 0; i < 13; i++) sum += Number(digits[i]) * (i % 2 === 0 ? 1 : 3);
  return sum % 10 === 0;
}

/** True for `YYYY`, `YYYY-MM` or `YYYY-MM-DD` that denote a real calendar date. */
export function isValidPublishedDate(value: string): boolean {
  const match = /^(\d{4})(?:-(\d{2})(?:-(\d{2}))?)?$/.exec(value);
  if (!match) return false;
  const year = Number(match[1]);
  if (match[2] === undefined) return true;
  const month = Number(match[2]);
  if (month < 1 || month > 12) return false;
  if (match[3] === undefined) return true;
  const day = Number(match[3]);
  return day >= 1 && day <= new Date(Date.UTC(year, month, 0)).getUTCDate();
}

/** Canonical BCP 47 form of a language tag, or undefined if it is not a valid tag. */
export function canonicalLanguageTag(value: string): string | undefined {
  try {
    return Intl.getCanonicalLocales(value)[0];
  } catch {
    return undefined;
  }
}

/** Unicode-aware lowercase key for case-insensitive text sorting (SQLite `NOCASE` folds ASCII only). */
export function sortKey(value: string): string {
  return value.normalize("NFC").toLowerCase();
}
